import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ContentCopy, Visibility, VisibilityOff } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { addSuccess } from "Redux/snackbarReducer";
import { useTranslations } from "hooks";
import { getTranslation } from "common";

interface ShowApiKeyOnceModalProps {
  apiKey: string | null;
  onClose: () => void;
}

/**
 * Modal that shows a freshly generated API key exactly once. The user is
 * expected to copy it to a password manager and confirm; the value is then
 * gone from the UI forever (the backend never returns it again).
 */
const ShowApiKeyOnceModal: React.FC<ShowApiKeyOnceModalProps> = ({
  apiKey,
  onClose,
}) => {
  const translations = useTranslations();
  const dispatch = useDispatch();
  const [hide, setHide] = useState(false);

  const handleCopy = async () => {
    if (!apiKey) return;
    try {
      await navigator.clipboard.writeText(apiKey);
      dispatch(
        addSuccess(
          getTranslation(translations, "profile.mcp.showonce.copied") ||
            "Copiato"
        )
      );
    } catch {
      // Clipboard can fail (insecure context, browser permission). Fall back
      // to selecting the text inside the input so the user can Ctrl+C.
    }
  };

  const handleConfirmClose = () => {
    const confirmText =
      getTranslation(translations, "profile.mcp.showonce.confirmPrompt") ||
      "Are you sure? You will not be able to see this key again.";
    if (window.confirm(confirmText)) {
      onClose();
    }
  };

  return (
    <Dialog
      open={Boolean(apiKey)}
      onClose={(_e, reason) => {
        // block backdrop click + escape; user must explicitly confirm
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {getTranslation(translations, "profile.mcp.showonce.title")}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {getTranslation(translations, "profile.mcp.showonce.warning")}
        </DialogContentText>
        <TextField
          fullWidth
          multiline
          minRows={2}
          value={apiKey || ""}
          variant="outlined"
          InputProps={{
            readOnly: true,
            sx: { fontFamily: "monospace", fontSize: 14 },
            endAdornment: (
              <InputAdornment position="end">
                <Stack direction="row" spacing={0.5}>
                  <Tooltip
                    title={
                      hide
                        ? getTranslation(translations, "profile.mcp.showonce.show") ||
                          "Show"
                        : getTranslation(
                            translations,
                            "profile.mcp.showonce.hide"
                          ) || "Hide"
                    }
                  >
                    <IconButton
                      onClick={() => setHide((v) => !v)}
                      size="small"
                      edge="end"
                    >
                      {hide ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={
                      getTranslation(
                        translations,
                        "profile.mcp.showonce.copy"
                      ) || "Copy"
                    }
                  >
                    <IconButton onClick={handleCopy} size="small" edge="end">
                      <ContentCopy />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </InputAdornment>
            ),
          }}
          type={hide ? "password" : "text"}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 1 }}
        >
          {getTranslation(translations, "profile.mcp.showonce.help") ||
            "This is the only time the full key will be shown. Store it in a safe place."}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCopy} startIcon={<ContentCopy />}>
          {getTranslation(translations, "profile.mcp.showonce.copy") || "Copy"}
        </Button>
        <Button onClick={handleConfirmClose} variant="contained" color="primary">
          {getTranslation(translations, "profile.mcp.showonce.confirm") ||
            "I saved it, close"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShowApiKeyOnceModal;
